import { useLoadingStore } from '~/stores/loading';
import { useProp000Store } from '~/stores/property-manager/prop-000';
import { handleResponse } from '~/composables/handleResponse';
import { HTTP_STATUS_CODES } from '~/constants/http-status-codes';
import { type Router } from 'vue-router';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type RequestOptions = {
  query?: Record<string, any>;
  body?: Record<string, any>;
  headers?: Record<string, string>;
};

export type ApiResponse<T> = T;

let refreshingPromise: Promise<void> | null = null;

const getHeaders = (customHeaders: Record<string, string> = {}, isAdmin: boolean = false): Record<string, string> => {
  const authStore = isAdmin ? useAdminUserStore() : usePropertyUserStore();
  const prop000Store = useProp000Store();

  const propertyId = prop000Store.GetPropertyId || localStorage.getItem('PROPERTY_ID');
  if (propertyId) customHeaders['PROPERTY-ID'] = propertyId;

  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...customHeaders
  };

  if (authStore.isAuthenticated) {
    headers = {
      Authorization: `Bearer ${authStore.accessToken}`,
      'username': encodeURIComponent(authStore?.userInfo?.name || ''),
      ...headers
    };
  }
  return headers;
};


const waitingQueue: (() => void)[] = [];

export const apiRequest = async <T>(
  method: HttpMethod,
  endpoint: string,
  options: RequestOptions = {},
  responseCallbacks?: ResponseCallbacks<T>
): Promise<T> => {
  const {
    public: { API_URL, API_BASE_PATH }
  } = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const router = nuxtApp.$router as Router;

  const isAdmin = window.location.pathname.startsWith('/admin');
  const authStore = isAdmin ? useAdminUserStore() : usePropertyUserStore();

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_URL}/${API_BASE_PATH}/${isAdmin ? `admin/${endpoint}` : endpoint}`;

  let capturedHeaders: Headers | undefined;

  const doFetch = () =>
    $fetch<T>(url, {
      method,
      mode: 'cors',
      query: options.query,
      headers: getHeaders(options.headers, isAdmin),
      body: options.body,
      onResponse({ response }) {
        capturedHeaders = response.headers;
      }
    });

  try {
    const executeWithRefresh = async (): Promise<T> => {
      try {
        const data = await doFetch();
        const isCSV = capturedHeaders?.get('Content-Type')?.includes('text/csv');
        const responseData = data instanceof Blob || isCSV ? { body: data } : { ...data };

        const handled = handleResponse({ ...responseData, headers: capturedHeaders }, responseCallbacks);
        if (!handled) throw new Error('Empty response');
        return handled;
      } catch (error: any) {
        const status = error.status || error.response?.status || 0;
        if (status !== 401) throw error;

        let refreshSuccess = true;

        try {
          if (refreshingPromise) {
            await new Promise<void>((resolve) => waitingQueue.push(resolve));
          } else {
            let resolvers: (() => void)[] = [];

            refreshingPromise = (async () => {
              try {
                await authStore.refreshAccessToken();
                resolvers = waitingQueue.splice(0);
              } catch (e) {
                refreshSuccess = false;

                waitingQueue.splice(0);

                authStore.logout();
              } finally {
                refreshingPromise = null;
                resolvers.forEach((cb) => cb());
              }
            })();

            await refreshingPromise;
          }

          if (!refreshSuccess) throw new Error('Refresh token failed');
        } catch {
          throw new Error('Refresh failed');
        }

        const retryData = await doFetch();
        const isCSV = capturedHeaders?.get('Content-Type')?.includes('text/csv');
        const responseData = retryData instanceof Blob || isCSV ? { body: retryData } : { ...retryData };

        const handled = handleResponse({ ...responseData, headers: capturedHeaders }, responseCallbacks);
        if (!handled) throw new Error('Empty retry response');
        return handled;
      }
    };

    return await executeWithRefresh();
  } catch (finalErr: any) {
    return handleApiError(finalErr, responseCallbacks, router);
  } finally {
  }
};

const handleApiError = (error: any, responseCallbacks?: ResponseCallbacks<any>, router?: Router) => {
  const status = error.status || error.response?.status || 0;
  const loading = useLoadingStore();
  if (status === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
    router?.push('/error');
  } else if ((error.name === 'FetchError' && error.message.includes('timeout')) || status === HTTP_STATUS_CODES.SERVICE_UNAVAILABLE) {
    loading.changeLoadingMessage('リクエストに失敗しました、ネットワークタイムアウト。');
  } else if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
    loading.changeLoadingMessage('ネットワーク接続エラー。');
  } else {
    const msg = error.response?._data?.message || 'リクエストに失敗しました';
    loading.changeLoadingMessage(msg);
  }

  return error;
};

export const useSocketApi = {
  get: async <T>(endpoint: string, params: object = {}, headers: Record<string, string> = {}) =>
    apiRequest<T>('GET', endpoint, { query: params, headers }),
  post: async <T>(endpoint: string, body: object = {}, headers: Record<string, string> = {}, responseCallbacks?: ResponseCallbacks<T>) =>
    apiRequest<T>('POST', endpoint, { body, headers }, responseCallbacks),
  put: async <T>(endpoint: string, body: object = {}, headers: Record<string, string> = {}) => apiRequest<T>('PUT', endpoint, { body, headers }),
  delete: async <T>(endpoint: string, headers: Record<string, string> = {}) => apiRequest<T>('DELETE', endpoint, { headers })
};
